/*
 * Usage (SPEC §18.1, decision 30): pure model of the page, no React and nothing that touches `window` or the clock. It
 * turns the API's plan and meters into what the page says (amounts, bars, the one banner), the month picker's options and
 * the breakdown rows. Plan limits show only once the org has a plan of its own (`plan.source === 'org'`); on the default
 * plan the page shows consumption only. Formatting is fixed to `en-US` so a number reads the same in every browser and in the tests.
 */
import { monthOf, monthsBack, type UsageMonth } from '@hangar/shared';
import type { MeterId, MeterLevel, UsageLimits, UsageMeter, UsagePlan, UsageReport, UsageRow } from '@/api/schemasUsage';

export const number = (n: number): string => Math.round(n).toLocaleString('en-US');

const UNITS = ['B', 'KiB', 'MiB', 'GiB', 'TiB'] as const;
/** Binary sizes (the plan's storage is in GiB): `0 B`, `4 KiB`, `21.5 GiB`. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < UNITS.length - 1) {
    value /= 1024;
    i += 1;
  }
  return i === 0 ? `${Math.round(value)} B` : `${value >= 100 ? Math.round(value) : value.toFixed(1).replace(/\.0$/, '')} ${UNITS[i]}`;
}

/** `45 s`, `12 min`, `1 h 25 min`, `0 s`. */
export function formatSeconds(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const minutes = Math.round(s / 60);
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** `2026-10` → `October 2026` (UTC: a month name never depends on the viewer's time zone). */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, 1)));
}

/** `2026-11-01T…` → `Nov 1` for "starts again on". */
export function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d);
}

// ───────────────────────────── meters ─────────────────────────────

export interface MeterView {
  id: MeterId;
  label: string;
  icon: string;
  /** "2,550 of 3,000 min", or "2,550 min" without a limit. */
  amount: string;
  /** What the limit is, for the line under the bar: "per month", "stored now", "running now". */
  caption: string;
  /** 0–100 for the bar; null when there is no limit (no bar). */
  bar: number | null;
  percentLabel: string;
  level: MeterLevel;
  /** The token the bar is drawn in (`bg-<tone>`): never a raw color. */
  tone: 'succeeded' | 'awaiting' | 'failed' | 'subtle';
  /** No plan of the org's own: the card shows what was used, without a bar or a percentage. */
  consumptionOnly: boolean;
}

const META: Record<MeterId, { label: string; icon: string; caption: string }> = {
  minutes: { label: 'Build minutes', icon: 'timer', caption: 'build service minutes this month, rounded up per build' },
  builds: { label: 'Builds', icon: 'construction', caption: 'builds this month, including those in flight' },
  storage: { label: 'Artifact storage', icon: 'cloud', caption: 'mirrored artifacts stored now' },
  concurrency: { label: 'Concurrent builds', icon: 'stacks', caption: 'builds running now; over it, builds wait' },
};

const TONE: Record<MeterLevel, MeterView['tone']> = { ok: 'succeeded', warn: 'awaiting', exceeded: 'failed', unlimited: 'subtle' };

/**
 * Whether the org has a plan of its own (set by Hangar's administrators). The API says `source: 'default'` while the org
 * runs on the configurable default plan, whose numbers are placeholders until plans are priced (SPEC decision 30, P16).
 */
export const hasPlan = (limits: Pick<UsageLimits, 'plan'>): boolean => limits.plan.source === 'org';

function amountOf(m: UsageMeter, withPlan: boolean): string {
  const fmt = (n: number) => (m.unit === 'bytes' ? formatBytes(n) : number(n));
  const unit = m.unit === 'min' ? ' min' : '';
  if (!withPlan) return `${fmt(m.used)}${unit}`;
  if (m.limit === null) return `${fmt(m.used)}${unit} · no limit`;
  return `${fmt(m.used)} of ${fmt(m.limit)}${unit}`;
}

/** One meter; without a plan of the org's own it is consumption only: no limit, no bar, no percentage, no level. */
export function meterView(m: UsageMeter, withPlan = true): MeterView {
  const meta = META[m.id];
  if (!withPlan) return { id: m.id, label: meta.label, icon: meta.icon, amount: amountOf(m, false), caption: meta.caption, bar: null, percentLabel: '', level: 'ok', tone: 'subtle', consumptionOnly: true };
  return {
    id: m.id, label: meta.label, icon: meta.icon, amount: amountOf(m, true), caption: meta.caption,
    bar: m.percent === null ? null : Math.min(100, Math.max(0, m.percent)), percentLabel: m.percent === null ? 'No limit' : `${m.percent}%`, level: m.level, tone: TONE[m.level], consumptionOnly: false,
  };
}

export const METER_ORDER: readonly MeterId[] = ['minutes', 'builds', 'storage', 'concurrency'];

export const meterViews = (limits: UsageLimits): MeterView[] => METER_ORDER.map((id) => meterView(limits.meters[id], hasPlan(limits)));

// ───────────────────────────── plan ─────────────────────────────

export interface PlanLine { label: string; value: string }

const limitText = (n: number | null, unit = ''): string => (n === null ? 'Unlimited' : `${number(n)}${unit}`);

/** The plan's limits as label / value lines. */
export function planLines(plan: UsagePlan): PlanLine[] {
  return [
    { label: 'Build minutes', value: `${limitText(plan.monthlyBuildMinutes)}${plan.monthlyBuildMinutes === null ? '' : ' / month'}` },
    { label: 'Builds', value: `${limitText(plan.monthlyBuilds)}${plan.monthlyBuilds === null ? '' : ' / month'}` },
    { label: 'Concurrent builds', value: limitText(plan.maxConcurrentBuilds) },
    { label: 'Artifact storage', value: plan.artifactStorageGb === null ? 'Unlimited' : `${number(plan.artifactStorageGb)} GiB` },
  ];
}

/** What happens at the limit, in one sentence. */
export function policyText(plan: UsagePlan): string {
  return plan.overQuota === 'block'
    ? 'At 100 % of minutes, builds or storage, new builds are paused. Running builds are never canceled.'
    : 'You are warned from 80 %. New builds keep running even over the limit.';
}

export const sourceText = (plan: UsagePlan): string => (plan.source === 'org' ? 'Set for your organization by your Hangar administrators' : 'No plan set for your organization yet');

// ───────────────────────────── the banner ─────────────────────────────

export interface UsageBanner {
  tone: 'destructive' | 'warning' | 'info';
  title: string;
  description: string;
}

const NAMES: Record<MeterId, string> = { minutes: 'build minutes', builds: 'builds', storage: 'artifact storage', concurrency: 'concurrent builds' };
const listOf = (ids: readonly MeterId[]): string => ids.map((id) => NAMES[id]).join(' and ');
/** "build minutes are", "artifact storage is": storage is the only singular meter. */
const verbOf = (ids: readonly MeterId[]): 'is' | 'are' => (ids.length === 1 && ids[0] === 'storage' ? 'is' : 'are');
const capital = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The one message the page leads with, or null when everything is fine. Without a plan of the org's own only a real
 * refusal is worth a banner (new builds paused); warnings against placeholder numbers are not shown.
 */
export function bannerOf(l: UsageLimits): UsageBanner | null {
  if (!l.blocked && !hasPlan(l)) return null;
  if (l.blocked) {
    const onlyStorage = l.exceeded.every((id) => id === 'storage');
    return {
      tone: 'destructive', title: 'New builds are paused',
      description: `Your plan's ${listOf(l.exceeded)} ${verbOf(l.exceeded)} used up. ${onlyStorage ? 'Delete artifacts, or ask your Hangar administrators to raise the plan.' : `The month starts again on ${dayLabel(l.resetsAt)} (UTC), or ask your Hangar administrators to raise the plan.`} Running builds are not affected.`,
    };
  }
  if (l.overrideActive && l.plan.allowUntil) {
    return { tone: 'info', title: 'Your Hangar administrators are allowing new builds over the plan', description: `Until ${dayLabel(l.plan.allowUntil)}${l.plan.allowReason ? `: ${l.plan.allowReason}` : ''}.` };
  }
  if (l.exceeded.length) return { tone: 'warning', title: 'Over the plan', description: `${capital(listOf(l.exceeded))} passed the plan's allowance. The plan only warns: new builds still run.` };
  const warn = (['minutes', 'builds', 'storage'] as const).filter((id) => l.meters[id].level === 'warn');
  if (warn.length) {
    const parts = warn.map((id) => `${NAMES[id]} at ${l.meters[id].percent ?? 0}%`);
    return { tone: 'warning', title: 'Nearing the plan', description: `${capital(parts.join(' and '))} of the plan's allowance.` };
  }
  return null;
}

// ───────────────────────────── months ─────────────────────────────

export interface MonthOption { value: string; label: string }

/** The picker's months: those with usage, the current one and the selected one, newest first (at least the last 3 when there is nothing yet). */
export function monthOptions(withUsage: readonly string[], current: string, selected: string | undefined): MonthOption[] {
  const set = new Set<string>([current, ...withUsage, ...(selected ? [selected] : [])]);
  if (set.size < 2) for (const m of monthsBack(current as UsageMonth, 3)) set.add(m);
  return [...set].sort().reverse().map((value) => ({ value, label: monthLabel(value) }));
}

export const currentMonthOf = (now: Date): string => monthOf(now);

/** The `month` search param: a real `YYYY-MM`, else undefined (the current month). */
export function monthParam(raw: unknown): string | undefined {
  return typeof raw === 'string' && /^[0-9]{4}-(0[1-9]|1[0-2])$/.test(raw) ? raw : undefined;
}

// ───────────────────────────── breakdown ─────────────────────────────

export type BreakdownTab = 'projects' | 'apps' | 'providers' | 'platforms';
export const BREAKDOWN_TABS: readonly BreakdownTab[] = ['projects', 'apps', 'providers', 'platforms'];
export const BREAKDOWN_LABEL: Record<BreakdownTab, string> = { projects: 'Projects', apps: 'Apps', providers: 'Providers', platforms: 'Platforms' };

/** The tabs a viewer gets: the provider breakdown is for platform operators only (SPEC decision 23). */
export const breakdownTabs = (seesProviders: boolean): readonly BreakdownTab[] => (seesProviders ? BREAKDOWN_TABS : BREAKDOWN_TABS.filter((t) => t !== 'providers'));

export interface BreakdownRow {
  key: string;
  label: string;
  sub: string | null;
  builds: number;
  minutes: number;
  providerTime: string;
  queued: string;
  storage: string;
  /** Share of the month's minutes, 0–100, for the bar. */
  share: number;
}

const PROVIDER_NAME: Record<string, string> = { eas: 'Expo EAS', bitrise: 'Bitrise' };
const PLATFORM_NAME: Record<string, string> = { ios: 'iOS', android: 'Android' };

function rowOf(base: Pick<BreakdownRow, 'key' | 'label' | 'sub'>, r: UsageRow, total: UsageRow): BreakdownRow {
  return {
    ...base, builds: r.builds, minutes: r.billableMinutes, providerTime: formatSeconds(r.providerSeconds), queued: formatSeconds(r.queuedSeconds), storage: formatBytes(r.artifactBytes),
    share: total.billableMinutes > 0 ? Math.round((r.billableMinutes / total.billableMinutes) * 100) : 0,
  };
}

export function breakdownRows(report: UsageReport, tab: BreakdownTab): BreakdownRow[] {
  const t = report.totals;
  switch (tab) {
    case 'projects': return report.byProject.map((r) => rowOf({ key: r.projectId, label: r.project ?? 'Unknown project', sub: null }, r, t));
    case 'apps': return report.byApp.map((r) => rowOf({ key: r.appId, label: r.app ?? 'Unknown app', sub: report.byProject.find((p) => p.projectId === r.projectId)?.project ?? null }, r, t));
    case 'providers': return report.byProvider.map((r) => rowOf({ key: `${r.provider}:${r.providerAccountId}`, label: PROVIDER_NAME[r.provider] ?? r.provider, sub: r.providerAccount }, r, t));
    case 'platforms': return report.byPlatform.map((r) => rowOf({ key: r.platform, label: PLATFORM_NAME[r.platform] ?? r.platform, sub: null }, r, t));
  }
}

/** A month with no finished build: the page shows its designed empty state instead of an empty table. */
export const isEmptyReport = (report: UsageReport): boolean => report.totals.finished === 0;

/** `hangar-usage-<org>-<month>.csv`, as the API names it. */
export const exportFilename = (orgSlug: string, month: string): string => `hangar-usage-${orgSlug}-${month}.csv`;
